import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Translate, withLocalize } from "react-localize-redux";
import { useOutletContext } from "react-router";
import Navigation from "../../../utils/components/Navigation";
import { BsPlusCircle, BsEyeFill } from "react-icons/bs";
import DataTable from "../../../utils/components/DataTable";
import NewCompanyGroupModal from "./components/NewCompanyGroupModal";
import GroupViewModal from "./components/GroupViewModal";
import { RiPencilFill } from "react-icons/ri";
import { MdDelete } from "react-icons/md";
import Loading from "../../../utils/components/Loading";
import { useProject } from "../../../utils/hooks/useProject";
import API from "../../../utils/API";
import toastr from "toastr";
import { confirmAlert } from "react-confirm-alert";
import AlertModal from "../../../utils/components/AlertModal";
import timeStampToDateFormat from "../../../utils/helpers/timestampToDateFormat";
import { useCan } from "../../../utils/hooks/useCan";
const CompanyGroups = ({ translate }) => {
  const {
    setBreadcrumbs,
    groups,
    refreshData,
    company,
    companyRoles,
    groupsLoading,
  } = useOutletContext();
  const [isModalOn, setIsModalOn] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedGroupId, setSelectedGroupId] = useState(null);

  const [groupViewInfo, setGroupViewInfo] = useState(null);
  const [isGroupViewModalOn, setIsGroupViewModalOn] = useState(false);

  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const { project } = useProject();

  const can = useCan();

  const columns = [
    {
      name: "#",
      selector: (row) => row.id,
      center: true,
      compact: true,
      maxWidth: "90px",
      cell: (_, index) => {
        return (page - 1) * pageSize + (index + 1);
      },
    },
    {
      name: <Translate id="company.groups.headers.group-name" />,
      selector: (row) => row.name,
      sortable: true,
      left: true,
      sortFunction: (a, b) => a.name.localeCompare(b.name),
    },
    {
      name: <Translate id="company.groups.headers.user-count" />,
      selector: (row) => row.user_count,
      sortable: true,
      center: true,
      cell: (row) => row.user_count,
    },
    {
      name: <Translate id="company.groups.headers.created-at" />,
      selector: (row) => row.created_at,
      sortable: true,
      center: true,
      format: (row) => timeStampToDateFormat(row.created_at, "dd.MM.yyyy"),
    },
    {
      name: <Translate id="company.groups.headers.operations" />,
      selector: (row) => row.id,
      center: true,
      cell: (row) => (
        <div className="actions-holder d-flex align-items-center justify-content-between">
          <div
            role="button"
            className="action-item"
            onClick={() => handleClickGroupView(row)}
          >
            <BsEyeFill />
          </div>
          {can.doThat("group:update") && (
            <div
              role="button"
              onClick={() => {
                setSelectedGroupId(row.id);
                setIsModalOn(true);
              }}
              className="action-item"
            >
              <RiPencilFill />
            </div>
          )}
          {can.doThat("group:update") && (
            <div
              role="button"
              className="action-item"
              onClick={() => handleClickDeleteGroupButton(row)}
            >
              <MdDelete />
            </div>
          )}
        </div>
      ),
    },
  ];

  useEffect(() => {
    if (!company?.id) return;

    if (users.length === 0 && isModalOn)
      API.getCompanyUsers(project?.id, company?.id, 1, 9999).then(
        ({ data: res }) => {
          setUsers(res.data);
        }
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOn, company?.id]);

  useEffect(() => {
    setIsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups]);

  useEffect(() => {
    setBreadcrumbs([
      {
        id: 0,
        title: "page_titles.companies",
        route: "auth.companies",
      },
      { id: 1, customTitle: company?.name || "" },
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company]);

  const handleClickGroupView = (row) => {
    const group = groups.find((group) => group.id === row.id);
    setGroupViewInfo({
      Name: group.name,
      "User Count": group.user_count,
      "Created At": timeStampToDateFormat(
        group.created_at,
        "dd MMMM yyyy HH:mm:ss"
      ),
    });

    setIsGroupViewModalOn(true);
  };

  const handleClickDeleteGroupButton = (row) => {
    confirmAlert({
      customUI: ({ onClose }) => (
        <AlertModal
          buttons={[
            {
              label: translate("cancel"),
              onClick: onClose,
              classNames: "btn btn-secondary",
            },
            {
              label: translate("delete"),
              onClick: () => {
                const url = `/groups/${row.id}`;
                API.deleteData(url).then(() => {
                  toastr.success("Group deleted successfully");
                  onClose();
                  refreshData();
                });
              },
              classNames: "btn btn-danger",
            },
          ]}
          message={translate("delete_confirmation")}
        />
      ),
    });
  };

  return (
    <div className="CompanyDetailPage CompanyGroups">
      <Helmet>
        <title>{translate("page_titles.company-groups")}</title>
      </Helmet>
      <header>
        <Navigation
          title={translate("company.groups.title")}
          buttonNode={
            project?.id && can.doThat("group:create") ? (
              <p className="m-0 p-0 text-center">
                <BsPlusCircle className="fs-4 me-2" />
                {translate("company.groups.new_group")}
              </p>
            ) : null
          }
          onButtonClick={() => {
            setSelectedGroupId(null);
            setIsModalOn(true);
          }}
        />
      </header>
      {isLoading ? (
        <Loading classNames="mt-3" text={translate("loading") + `...`} />
      ) : (
        <DataTable
          pagination
          data={groups || []}
          columns={columns}
          progressPending={isLoading || groupsLoading}
          progressComponent={
            <div className="py-3">
              <Loading classNames={"my-3"} />
            </div>
          }
          noDataComponent={
            <div className="text-center py-3">
              {translate("company.users.no_users")}
            </div>
          }
          onChangePage={(page) => setPage(page)}
          onChangeRowsPerPage={(currentRowsPerPage, currentPage) => {
            setPageSize(currentRowsPerPage);
            setPage(currentPage);
          }}
        />
      )}
      <NewCompanyGroupModal
        handleClose={() => {
          setIsModalOn(false);
          setSelectedGroupId(null);
        }}
        selectedGroupId={selectedGroupId}
        users={users}
        show={isModalOn}
        refreshData={refreshData}
        roles={companyRoles}
        project={project}
        translate={translate}
      />
      <GroupViewModal
        show={isGroupViewModalOn}
        hideModal={() => setIsGroupViewModalOn(false)}
        groupInfo={groupViewInfo}
      />
    </div>
  );
};

export default withLocalize(CompanyGroups);
